import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";
import api from "@/lib/api";

export default function Login() {
  const { loginEmail, registerEmail, lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/enter";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await loginEmail(email, password);
      else await registerEmail(email, password, name);
      toast.success(t("toast.welcome"));
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.login_fail"));
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const forgotPassword = async () => {
    const em = (email || "").trim();
    if (!em) { toast.error(t("auth.forgot_need_email")); return; }
    setBusy(true);
    try {
      await api.post("/auth/request-password-reset", {
        email: em, origin_url: window.location.origin,
      });
      // Always show generic success to avoid revealing which emails exist.
      toast.success(t("auth.forgot_sent"));
    } catch {
      toast.success(t("auth.forgot_sent"));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto pt-6">
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">{t("auth.entry")}</div>
      <h1 className="serif text-4xl text-stone-100 mt-2">{mode === "login" ? t("auth.login") : t("auth.new")}</h1>
      <p className="text-stone-400 mt-2 text-sm">{t("auth.private")}</p>

      <button data-testid="google-login-btn" onClick={googleLogin} className="mt-8 ghost-btn w-full py-3 border-stone-700 hover:border-amber-500/50">
        <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        {t("auth.google")}
      </button>

      <div className="flex items-center gap-3 my-6">
        <div className="h-px bg-stone-800 flex-1"/>
        <div className="mono text-[10px] tracking-widest text-stone-500">{t("auth.or_email")}</div>
        <div className="h-px bg-stone-800 flex-1"/>
      </div>

      <form onSubmit={submit} className="space-y-3">
        {mode === "register" && (
          <div>
            <label className="label-cap">{t("auth.name")}</label>
            <input data-testid="auth-name" type="text" required value={name} onChange={(e)=>setName(e.target.value)} className="script-input mt-1" placeholder={lang === "pl" ? "np. Anna" : "e.g. Anna"}/>
          </div>
        )}
        <div>
          <label className="label-cap">{t("auth.email")}</label>
          <input data-testid="auth-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="script-input mt-1" placeholder="you@studio.com"/>
        </div>
        <div>
          <label className="label-cap">{t("auth.password")}</label>
          <input data-testid="auth-password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} className="script-input mt-1" placeholder={t("auth.password_hint")}/>
        </div>
        <button data-testid="auth-submit" type="submit" disabled={busy} className="pill-btn w-full mt-2">
          {busy ? t("auth.wait") : (mode === "login" ? t("auth.enter_btn") : t("auth.create_btn"))}
        </button>
      </form>

      {mode === "login" && (
        <div className="mt-3 text-sm text-stone-400">
          <button
            type="button"
            data-testid="auth-forgot-password"
            className="text-amber-500 hover:text-amber-400"
            onClick={forgotPassword}
          >
            {t("auth.forgot_password")}
          </button>
        </div>
      )}

      <div className="mt-6 text-sm text-stone-400">
        {mode === "login" ? (
          <>{t("auth.new_here")} <button data-testid="switch-register" className="text-amber-500 hover:text-amber-400" onClick={()=>setMode("register")}>{t("auth.create_link")}</button></>
        ) : (
          <>{t("auth.have_account")} <button data-testid="switch-login" className="text-amber-500 hover:text-amber-400" onClick={()=>setMode("login")}>{t("auth.login_link")}</button></>
        )}
      </div>

      <div className="mt-8 text-xs text-stone-500">
        <Link to="/" className="hover:text-stone-300">{t("auth.back")}</Link>
      </div>
    </div>
  );
}
